import { reportError } from '../services/telemetry'

/**
 * Ramène à une liste ce que l'API a envoyé, quoi qu'elle ait envoyé.
 *
 * Le motif `charge?.champ ?? []` est partout dans le panneau. Il protège
 * de `null`, pas d'un objet — et c'est un objet qui est arrivé. Le cache
 * de l'API rendait ses collections en `__PHP_Incomplete_Class`, que
 * `json_encode` écrit `{"__PHP_Incomplete_Class_Name":"…"}` : un objet là
 * où l'écran attendait une liste. `recentOrders.map` levait alors une
 * TypeError, et le tableau de bord entier disparaissait derrière la page
 * d'erreur — pour huit lignes de commandes récentes.
 *
 * La cause est corrigée côté API. Ceci est la ceinture : un écran ne doit
 * pas s'effondrer parce qu'une charge a changé de forme. Il en affiche ce
 * qu'il peut, et le signale.
 *
 * @param {unknown} valeur  ce que l'API a envoyé
 * @param {string}  nom     le nom du champ, tel qu'il apparaîtra dans l'alerte
 * @returns {Array}
 */
export function liste(valeur, nom) {
  if (Array.isArray(valeur)) return valeur
  if (valeur == null) return []

  signaler(valeur, nom)

  /*
   | Une collection PHP indexée par clés — {3: {…}, 7: {…}} — reste
   | affichable : ses valeurs sont les lignes attendues. On ne la déplie
   | que si TOUTES ses valeurs sont des objets, sinon on rendrait des
   | lignes de n'importe quoi. Le marqueur de classe incomplète tombe
   | précisément dans ce « sinon » : sa seule valeur est une chaîne.
   */
  if (typeof valeur === 'object') {
    const valeurs = Object.values(valeur)

    if (valeurs.length > 0 && valeurs.every(v => v !== null && typeof v === 'object')) {
      return valeurs
    }
  }

  return []
}

/**
 * Prévient, une fois.
 *
 * telemetry.js ne rapporte qu'une signature par session et plafonne à dix
 * envois : un écran qui se rafraîchit toutes les trente secondes avec une
 * charge malformée ne produit donc pas trente rapports par minute.
 */
function signaler(valeur, nom) {
  const forme = typeof valeur === 'object'
    ? `objet {${Object.keys(valeur).slice(0, 3).join(', ')}}`
    : typeof valeur

  reportError(
    new TypeError(`${nom} : ${forme} reçu là où une liste était attendue`),
    { source: 'forme-de-charge' },
  )
}
